import { getBytes, ref, uploadBytes } from 'firebase/storage';
import { auth, storage } from '../../firebase/firebase';
import type { LCQuestion } from './types';

export type BankFile = { version: number; questions: LCQuestion[] };
export type BanksBundle = { version: number; banks: [BankFile, BankFile, BankFile] };

export type LCBankIndex = 1 | 2 | 3;

const BASE_PATH = 'adminBanks/y3/language-conventions';
const BANK_PATHS: Record<LCBankIndex, string> = {
  1: `${BASE_PATH}/lc_bank_1.json`,
  2: `${BASE_PATH}/lc_bank_2.json`,
  3: `${BASE_PATH}/lc_bank_3.json`,
};
const META_PATH = `${BASE_PATH}/meta.json`;

type MetaFile = { version: number; updatedAt: string; updatedByUid: string };

let cachedBundle: BanksBundle | null = null;
let cachedAtMs = 0;
let inFlightPromise: Promise<BanksBundle> | null = null;

async function ensureAuthTokenReady(): Promise<void> {
  const user = auth.currentUser;
  if (!user) return;
  try {
    // Prime/refresh ID token so Storage requests are authenticated.
    await user.getIdToken();
  } catch {
    // If token fetch fails, Storage will still throw a meaningful error.
  }
}

function decodeUtf8(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  try {
    return new TextDecoder('utf-8').decode(view);
  } catch {
    let out = '';
    for (let i = 0; i < view.length; i++) out += String.fromCharCode(view[i]);
    return out;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isChoiceTuple4(value: unknown): value is [string, string, string, string] {
  return Array.isArray(value) && value.length === 4 && value.every((v) => typeof v === 'string');
}

function isCorrectIndex(value: unknown): value is 0 | 1 | 2 | 3 {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

function coerceQuestion(value: unknown): LCQuestion | null {
  if (!isRecord(value)) return null;
  if (!isString(value.id)) return null;
  if (!isString(value.type)) return null;
  if (!isString(value.prompt)) return null;
  if (!isString(value.skill)) return null;

  if (value.type === 'mcq') {
    if (!isChoiceTuple4(value.choices)) return null;
    if (!isCorrectIndex(value.correctIndex)) return null;
    const sentence = isString(value.sentence) ? value.sentence : undefined;
    return {
      id: value.id,
      type: 'mcq',
      prompt: value.prompt,
      sentence,
      choices: value.choices,
      correctIndex: value.correctIndex,
      skill: value.skill as any,
    };
  }

  if (value.type === 'spell') {
    if (!isString(value.sentenceWithError)) return null;
    if (!isString(value.errorToken)) return null;
    if (!isString(value.correctToken)) return null;
    return {
      id: value.id,
      type: 'spell',
      prompt: value.prompt,
      sentenceWithError: value.sentenceWithError,
      errorToken: value.errorToken,
      correctToken: value.correctToken,
      skill: 'spelling',
    };
  }

  if (value.type === 'selectIncorrect') {
    if (!isString(value.sentence)) return null;
    if (!Array.isArray(value.tokens) || !value.tokens.every((t) => typeof t === 'string')) return null;
    if (typeof value.incorrectIndex !== 'number') return null;
    if (!isString(value.correctToken)) return null;
    return {
      id: value.id,
      type: 'selectIncorrect',
      prompt: value.prompt,
      sentence: value.sentence,
      tokens: value.tokens,
      incorrectIndex: value.incorrectIndex,
      correctToken: value.correctToken,
      skill: value.skill as any,
    };
  }

  return null;
}

function validateQuestionsArray(value: unknown): LCQuestion[] {
  if (!Array.isArray(value)) return [];
  const out: LCQuestion[] = [];
  const seen = new Set<string>();

  for (const row of value) {
    const q = coerceQuestion(row);
    if (!q) continue;
    if (seen.has(q.id)) continue;
    seen.add(q.id);
    out.push(q);
  }

  return out;
}

async function downloadJson(path: string): Promise<unknown> {
  await ensureAuthTokenReady();
  try {
    const bytes = await getBytes(ref(storage, path));
    const text = decodeUtf8(bytes);
    return JSON.parse(text);
  } catch (e: any) {
    const code = String(e?.code ?? '');
    if (code === 'storage/object-not-found') {
      throw new Error(
        `Missing bank file in Firebase Storage: ${path}. ` +
          `An owner must upload it (Owner → LC Banks) before students can load practice.`
      );
    }
    throw e;
  }
}

async function downloadBankFile(index: LCBankIndex, version: number): Promise<BankFile> {
  const parsed = await downloadJson(BANK_PATHS[index]);
  const questions = validateQuestionsArray(parsed);
  if (questions.length === 0) {
    throw new Error(`Bank ${index} is missing or malformed (no valid questions)`);
  }
  return { version, questions };
}

async function downloadMeta(): Promise<MetaFile | null> {
  try {
    const parsed = await downloadJson(META_PATH);
    if (!isRecord(parsed)) return null;
    if (typeof parsed.version !== 'number') return null;
    if (!isString(parsed.updatedAt)) return null;
    if (!isString(parsed.updatedByUid)) return null;
    return parsed as MetaFile;
  } catch {
    return null;
  }
}

export async function loadLanguageConventionsMetaVersion(): Promise<number> {
  const meta = await downloadMeta();
  return meta?.version ?? 1;
}

export async function loadLanguageConventionsBank(bankIndex: LCBankIndex): Promise<BankFile> {
  const version = await loadLanguageConventionsMetaVersion();
  return downloadBankFile(bankIndex, version);
}

export function invalidateLanguageConventionsBanksCache(): void {
  cachedBundle = null;
  cachedAtMs = 0;
  inFlightPromise = null;
}

export async function loadLanguageConventionsBanks(): Promise<BanksBundle> {
  if (cachedBundle) return cachedBundle;
  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = (async () => {
    const version = await loadLanguageConventionsMetaVersion();

    const [b1, b2, b3] = await Promise.all([
      downloadBankFile(1, version),
      downloadBankFile(2, version),
      downloadBankFile(3, version),
    ]);

    const bundle: BanksBundle = { version, banks: [b1, b2, b3] };
    cachedBundle = bundle;
    cachedAtMs = Date.now();
    return bundle;
  })();

  try {
    return await inFlightPromise;
  } finally {
    inFlightPromise = null;
  }
}

export async function saveLanguageConventionsBank(
  bankIndex: LCBankIndex,
  questions: LCQuestion[]
): Promise<void> {
  const payload = JSON.stringify(questions, null, 2);
  const bytes = new TextEncoder().encode(payload);
  await ensureAuthTokenReady();
  await uploadBytes(ref(storage, BANK_PATHS[bankIndex]), bytes, {
    contentType: 'application/json; charset=utf-8',
  });
}

export async function saveLanguageConventionsMeta(params: { version: number; updatedAt: string; updatedByUid: string }): Promise<void> {
  const payload = JSON.stringify(params, null, 2);
  const bytes = new TextEncoder().encode(payload);
  await ensureAuthTokenReady();
  await uploadBytes(ref(storage, META_PATH), bytes, {
    contentType: 'application/json; charset=utf-8',
  });
}

export function getLanguageConventionsBankPaths(): { base: string; meta: string; banks: Record<LCBankIndex, string> } {
  return { base: BASE_PATH, meta: META_PATH, banks: BANK_PATHS };
}
